import React from "react";

import { Card, CardContent, Chip, Collapse, Divider, Grid, Typography, useTheme } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

import useColourPalette from "components/visuals/useColourPalette";

import { FootfallByArea } from "modules/customer/tools/location/footfallNew/footfallByArea";
import numberFormatter from "utils/numberFormatter";
import { FootfallMeasureType } from "modules/customer/insights/portfolioNew/footfallNew/footfallMeasureType";

const StyledCard = withStyles(theme => ({
    root: {
        position: "absolute",
        zIndex: theme.zIndex.drawer,
        margin: theme.spacing(2),
        bottom: theme.spacing(2),
        width: theme.spacing(26),
        // @ts-ignore
        backgroundColor: theme.palette.background.insight
    }
}))(Card);

const StyledCardContent = withStyles(theme => ({
    root: {
        "&:last-child": {
            padding: theme.spacing(2)
        }
    }
}))(CardContent);

const StyledChip = withStyles(theme => ({
    root: {
        backgroundColor: (props: any) => props["data-background-color"],
        width: theme.spacing(3),
        height: theme.spacing(1.5)
    }
}))(Chip);

const StyledDivider = withStyles(theme => ({
    root: {
        // @ts-ignore
        backgroundColor: theme.palette.quaternary.light,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    }
}))(Divider);

interface LegendProps {
    cell?: FootfallByArea,
    measureType: FootfallMeasureType,
    thresholds: number[]
}

const Legend: React.FC<LegendProps> = (props) => {
    const { cell, measureType, thresholds } = props;
    const theme = useTheme();
    const colourPalette = useColourPalette();

    const showVisits = measureType === FootfallMeasureType.NumberOfVisits;
    const visitsTitle = "Visits in the last 12 months:";
    const dwellTimeTitle = "Average dwell time in the last 12 months:";
    const visitsValue = numberFormatter.toDecimalPlaces(cell?.numberOfVisits, 1);
    const dwellTimeValue = `${numberFormatter.toDecimalPlaces(cell?.dwellTime, 0)} minutes`;

    const legendTitle = showVisits
        ? "Visits in the last 12 months"
        : "Average dwell time in the last 12 months (Minutes)";

    return (
        <StyledCard elevation={0}>
            <StyledCardContent>
                <Collapse in={cell !== undefined}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography variant="body2" component="div">
                                {showVisits ? visitsTitle : dwellTimeTitle}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {showVisits ? visitsValue : dwellTimeValue}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" component="div">
                                {showVisits ? dwellTimeTitle : visitsTitle}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {showVisits ? dwellTimeValue : visitsValue}
                            </Typography>
                        </Grid>
                    </Grid>
                    <StyledDivider />
                </Collapse>
                <Typography variant="body1" component="div" gutterBottom style={{ color: theme.palette.text.disabled }}>
                    {legendTitle}
                </Typography>
                <Typography variant="body1" component="div" gutterBottom>
                    <StyledChip size="small" data-background-color={colourPalette.sequential[0]} />
                    &nbsp;&nbsp;Very high&nbsp;{"(> " + numberFormatter.toDecimalPlaces(thresholds[4], 1) + ")"}
                </Typography>
                <Typography variant="body1" component="div" gutterBottom>
                    <StyledChip size="small" data-background-color={colourPalette.sequential[1]} />
                    &nbsp;&nbsp;High&nbsp;{"(" + (numberFormatter.toDecimalPlaces(thresholds[3], 1) + " - " + numberFormatter.toDecimalPlaces(thresholds[4], 1)) + ")"}
                </Typography>
                <Typography variant="body1" component="div" gutterBottom>
                    <StyledChip size="small" data-background-color={colourPalette.sequential[2]} />
                    &nbsp;&nbsp;Medium&nbsp;{"(" + (numberFormatter.toDecimalPlaces(thresholds[2], 1) + " - " + numberFormatter.toDecimalPlaces(thresholds[3], 1)) + ")"}
                </Typography>
                <Typography variant="body1" component="div" gutterBottom>
                    <StyledChip size="small" data-background-color={colourPalette.sequential[3]} />
                    &nbsp;&nbsp;Low&nbsp;{"(" + (numberFormatter.toDecimalPlaces(thresholds[1], 1) + " - " + numberFormatter.toDecimalPlaces(thresholds[2], 1)) + ")"}
                </Typography>
                <Typography variant="body1" component="div">
                    <StyledChip size="small" data-background-color={colourPalette.sequential[4]} />
                    &nbsp;&nbsp;Very low&nbsp;{"(< " + numberFormatter.toDecimalPlaces(thresholds[1], 1) + ")"}
                </Typography>
            </StyledCardContent>
        </StyledCard>
    );
};

export default Legend;
